import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {isDefined, parsePath} from 'Quoting/utils/utilities';
import {Map as ImmutableMap, List as ImmutableList, fromJS} from 'immutable';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {createGHI3BenefitStructure} from '../../../PlanTemplates/HI/GHI3';
import {
  getPlanConfigurationOptionsWithRestriction,
  getSelectedBenefitOption,
} from '../../../PlanTemplates/HI/planOptionConfiguration';
import {
  getDailyHospitalConfinementPropertyPaths,
  getDailyIntensiveCareConfinementPaths,
  getDailyStepdownIntensiveCareConfinementPaths,
  getHosAdmissionRoutineNewbornPropertyPaths,
  getHosConfinementRoutineNewbornPropertyPaths,
  getHospitalCareServicesPropertyPaths,
  getIntensiveCareAdmissionPaths,
  getObservationUnitPaths,
  getStepdownIntensiveCareAdmissionPaths,
} from './propertyPaths';

const ValidationKey = {
  HIHosAdmissionBenefitAmount: 'HIHosAdmissionBenefit.benefitAmount',
  HIHosConfinementBenefitAmount: 'HIHosAdmissionBenefit.benefitAmount',
  HIICUConfinementBenefitAmount: 'HIICUConfinementBenefit.benefitAmount',
  HIConfinementRoutineNewBornBenefit: 'HIConfinementRoutineNewBornBenefit.confinementLimit',
};

const HIHosCareServicesOptionSelectionKey = 'HIHosCareServicesOptionSelection';

const getBenefitDetailsObject = (
  benefitDetailsObjectName: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  benefitStructureFn: (...args: any[]) => Record<string, any>,
  planRootId?: string,
  coverageDefParentId?: string
) => {
  const benefitStructure: Record<string, any> = benefitStructureFn(
    defaultValues ?? ImmutableMap<string, any>(),
    planRootId ?? '',
    coverageDefParentId ?? ''
  );
  return benefitStructure[benefitDetailsObjectName];
};

const HospitalCareServicesBenefitOptionSelection = () => {
  const {selectedPlan, quoteFormErrors, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();

  const selectedCoverageIndex = 0;

  const situsStateCd = getQuoteFieldValue('riskStateCd') ?? '';

  const plans = getQuoteFieldValue<ImmutableList<any>>('benefitPackagingDetail.plans');

  const selectedAmounts: ImmutableList<{
    hosAdmission: number;
    hosConfinement: number;
    icuConfinement: number;
  }> = ImmutableList.isList(plans)
    ? plans?.map((plan: ImmutableMap<string, any>) => ({
        hosAdmission: plan.getIn(
          parsePath('coverageDefs[0].benefitStructure.hosAdmissionDetails.benefitAmount.amount')
        ) as number,
        hosConfinement: plan.getIn(
          parsePath('coverageDefs[0].benefitStructure.hosConfinementDetails.benefitAmount.amount')
        ) as number,
        icuConfinement: plan.getIn(
          parsePath('coverageDefs[0].benefitStructure.icuConfinementDetails.benefitAmount.amount')
        ) as number,
      }))
    : ImmutableList();

  const ghi3ConfinementBenefitOptions = getPlanConfigurationOptionsWithRestriction(
    'GHI3',
    situsStateCd,
    selectedAmounts,
    selectedPlan?.planIndex ?? 0,
    [0, 3] // restrict option1 and option4 combination
  );

  const hosAdmissionPropertyPaths = getHospitalCareServicesPropertyPaths(selectedPlan?.planIndex);

  const hosAdmissionNewbornPaths = getHosAdmissionRoutineNewbornPropertyPaths(selectedPlan?.planIndex, 0);

  const hosConfinementPropertyPaths = getDailyHospitalConfinementPropertyPaths(selectedPlan?.planIndex);

  const hosConfinementNewbornPaths = getHosConfinementRoutineNewbornPropertyPaths(selectedPlan?.planIndex);

  const icuConfinementPropertyPaths = getIntensiveCareAdmissionPaths(selectedPlan?.planIndex);

  const icuStepdownAdmissionPaths = getStepdownIntensiveCareAdmissionPaths(selectedPlan?.planIndex);

  const observationUnitPaths = getObservationUnitPaths(selectedPlan?.planIndex);

  const dailyICUConfinementPaths = getDailyIntensiveCareConfinementPaths(selectedPlan?.planIndex);

  const dailyStepdownICUConfinementPaths = getDailyStepdownIntensiveCareConfinementPaths(selectedPlan?.planIndex);

  const planRootId =
    getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]._key.rootId`) ?? '';

  const coverageDefId =
    getQuoteFieldValue(
      `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}].coverageDefs[${selectedCoverageIndex}]._key.id`
    ) ?? '';

  const [selectedConfinementBenefitOption, setSelectedConfinementBenefitOption] = useState<any>(() =>
    getSelectedBenefitOption(
      'GHI3',
      {
        hosAdmission: getQuoteFieldValue(hosAdmissionPropertyPaths.benefitAmount),
        hosConfinement: getQuoteFieldValue(hosConfinementPropertyPaths.benefitAmount),
        icuConfinement: getQuoteFieldValue(icuConfinementPropertyPaths.benefitAmount),
      },
      situsStateCd
    )
  );

  const handleConfinementBenefitsOptionSelection = (value: any) => {
    setSelectedConfinementBenefitOption(value);

    let confinementFields = [
      {
        path: hosAdmissionPropertyPaths.benefitAmount,
        newValue: value.hosAdmission,
      },
      {
        path: hosConfinementPropertyPaths.benefitAmount,
        newValue: value.hosConfinement,
      },
    ];

    const icuConfinementFields = getICUConfinementBenefitDetails(
      value.icuConfinement,
      defaultPlanValues.get('GHI3') ?? ImmutableMap<string, any>(),
      planRootId,
      coverageDefId,
      selectedPlan?.planIndex
    );

    // As per NJ state requirements
    let otherIcuBenefits = [];
    let newbornBenefits = [];
    if (situsStateCd === 'NJ') {
      otherIcuBenefits = [
        {
          path: dailyICUConfinementPaths.benefitAmount,
          newValue: value.dailyICUConfinement,
        },
        {
          path: dailyStepdownICUConfinementPaths.benefitAmount,
          newValue: value.dailyStepdownICUConfinement,
        },
      ];
    } else {
      otherIcuBenefits = [
        {
          path: icuStepdownAdmissionPaths.benefitAmount,
          newValue: 1.5 * value.hosAdmission,
        },
        {
          path: dailyICUConfinementPaths.benefitAmount,
          newValue: 2 * value.hosConfinement,
        },
        {
          path: dailyStepdownICUConfinementPaths.benefitAmount,
          newValue: 1.5 * value.hosConfinement,
        },
      ];
      if (situsStateCd !== 'ID') {
        otherIcuBenefits.push({
          path: observationUnitPaths.benefitAmount,
          newValue: value.hosConfinement,
        });
      }
      if (['TX', 'AR', 'IN'].includes(situsStateCd)) {
        newbornBenefits = [
          {
            path: hosAdmissionNewbornPaths.routineNewbornAmount,
            newValue: value.hosAdmissionNewborn,
          },
          {
            path: hosConfinementNewbornPaths.routineNewbornAmount,
            newValue: value.hosConfinementNewborn,
          },
        ];
      }
    }

    confinementFields = confinementFields.concat(newbornBenefits);
    confinementFields = confinementFields.concat(icuConfinementFields);
    confinementFields = confinementFields.concat(otherIcuBenefits);

    updateQuoteFieldValues(confinementFields);
  };

  const rows: GridRowProps[] = [
    {
      rowProps: {key: `gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`},
      items: [
        {
          key: `gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Plan Options"
              placeholder="Please select the required plan option"
              id={`gim2-plan-options-${selectedPlan?.planIndex}-${selectedPlan?.planKey}`}
              value={selectedConfinementBenefitOption}
              options={ghi3ConfinementBenefitOptions}
              onChange={e => handleConfinementBenefitsOptionSelection(e.target.value)}
              required
              error={Boolean(quoteFormErrors?.[HIHosCareServicesOptionSelectionKey])}
              helperText={quoteFormErrors ? quoteFormErrors?.[HIHosCareServicesOptionSelectionKey] : undefined}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const HospitalAdmission = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);

  const hospitalAdmissionPaths = getHospitalCareServicesPropertyPaths(selectedPlan?.planIndex, 0);

  const handleValiationOnBlur = useQuoteFormValidation();

  const rows: GridRowProps[] = [
    {
      rowProps: {key: `HIHospitalAdmission-row1`},
      items: [
        {
          key: hospitalAdmissionPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              id={hospitalAdmissionPaths.benefitAmount}
              value={getQuoteFieldValue<number>(hospitalAdmissionPaths.benefitAmount)}
              onChange={e => {
                if (!isNaN(Number(e.target.value))) {
                  updateQuoteFieldValue(
                    hospitalAdmissionPaths.benefitAmount,
                    fromJS(getMoneyObject(Number(e.target.value)))
                  );
                }
              }}
              onBlur={handleValiationOnBlur}
              error={Boolean(quoteFormErrors?.[ValidationKey.HIHosAdmissionBenefitAmount])}
              helperText={quoteFormErrors ? quoteFormErrors?.[ValidationKey.HIHosAdmissionBenefitAmount] : undefined}
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: hospitalAdmissionPaths.firstDayConfinementPaymentFreqLimitCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosAdmissionBenefit.firstDayConfinementPaymentFreqLimitCd"
              label="Payment Frequency Limit"
              id={hospitalAdmissionPaths.firstDayConfinementPaymentFreqLimitCd}
              value={getQuoteFieldValue(hospitalAdmissionPaths.firstDayConfinementPaymentFreqLimitCd)}
              onChange={e =>
                updateQuoteFieldValue(
                  hospitalAdmissionPaths.firstDayConfinementPaymentFreqLimitCd,
                  e.target.value as string
                )
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const HospitalAdmissionRoutineNewborn = () => {
  const {getQuoteFieldValue} = useQuoteField();
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);

  const hosAdmissionNewbornPaths = getHosAdmissionRoutineNewbornPropertyPaths(selectedPlan?.planIndex, 0);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'hospital-admission-routine-newborn-row-1'},
      items: [
        {
          key: 'hospital-admission-routine-newborn-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={getQuoteFieldValue<number>(hosAdmissionNewbornPaths.routineNewbornAmount)}
              adornment="money"
              required
              disabled
              id={'hospitalAdmissionRoutineNewbornBenefitAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'hospital-admission-routine-newborn-maxlimit-child',
          element: (
            <QuoteFormElement
              elementType="Select"
              options={[{displayValue: '1', code: '1', orderNo: 0}]}
              label="Max Limit/Child"
              id={hosAdmissionNewbornPaths.maxLimit}
              value={getQuoteFieldValue(hosAdmissionNewbornPaths.maxLimit)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const DailyHospitalConfinement = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const dailyHospitalConfinementPaths = getDailyHospitalConfinementPropertyPaths(selectedPlan?.planIndex);
  const handleValiationOnBlur = useQuoteFormValidation();

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'HIHosConfinementBenefit-row1'},
      items: [
        {
          key: dailyHospitalConfinementPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              id={dailyHospitalConfinementPaths.benefitAmount}
              value={getQuoteFieldValue<number>(dailyHospitalConfinementPaths.benefitAmount)}
              onChange={e => {
                if (!isNaN(Number(dailyHospitalConfinementPaths.benefitAmount))) {
                  updateQuoteFieldValue(
                    dailyHospitalConfinementPaths.benefitAmount,
                    fromJS(getMoneyObject(Number(e.target.value)))
                  );
                }
              }}
              onBlur={handleValiationOnBlur}
              error={Boolean(quoteFormErrors?.[ValidationKey.HIHosConfinementBenefitAmount])}
              helperText={quoteFormErrors ? quoteFormErrors?.[ValidationKey.HIHosConfinementBenefitAmount] : undefined}
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: dailyHospitalConfinementPaths.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIHosConfinementBenefit.maxBenefitNumber"
              id={dailyHospitalConfinementPaths.maxBenefitNumber}
              label="Max # Days Limit (Per Confinement)"
              value={getQuoteFieldValue<number>(dailyHospitalConfinementPaths.maxBenefitNumber)}
              onChange={e =>
                updateQuoteFieldValue(dailyHospitalConfinementPaths.maxBenefitNumber, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const DailyHospitalConfinementRoutineNewborn = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const hosConfinementNewbornPaths = getHosConfinementRoutineNewbornPropertyPaths(selectedPlan?.planIndex);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'daily-hospital-confinement-routine-newborn-row-1'},
      items: [
        {
          key: 'daily-hospital-confinement-routine-newborn-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              id={hosConfinementNewbornPaths.routineNewbornAmount}
              value={getQuoteFieldValue<number>(hosConfinementNewbornPaths.routineNewbornAmount)}
              error={Boolean(quoteFormErrors?.[ValidationKey.HIHosConfinementBenefitAmount])}
              helperText={quoteFormErrors ? quoteFormErrors?.[ValidationKey.HIHosConfinementBenefitAmount] : undefined}
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'daily-hospital-confinement-routine-newborn-max-days-limit-confinement',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIConfinementRoutineNewBornBenefit.confinementLimit"
              id={hosConfinementNewbornPaths.confinementLimit}
              label="Max # Days Limit (Per Confinement)"
              value={getQuoteFieldValue<number>(hosConfinementNewbornPaths.confinementLimit)}
              onChange={e =>
                updateQuoteFieldValue(hosConfinementNewbornPaths.confinementLimit, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const IntensiveCareAdmission = () => {
  const {selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();

  const intensiveCareAdmissionPaths = getIntensiveCareAdmissionPaths(selectedPlan?.planIndex ?? 0);
  const handleValiationOnBlur = useQuoteFormValidation();

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'HIICUConfinementBenefit-row1'},
      items: [
        {
          key: intensiveCareAdmissionPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              id={intensiveCareAdmissionPaths.benefitAmount}
              value={getQuoteFieldValue<number>(intensiveCareAdmissionPaths.benefitAmount)}
              onChange={e => {
                if (!isNaN(Number(intensiveCareAdmissionPaths.benefitAmount))) {
                  updateQuoteFieldValue(
                    intensiveCareAdmissionPaths.benefitAmount,
                    fromJS(getMoneyObject(Number(e.target.value)))
                  );
                }
              }}
              onBlur={handleValiationOnBlur}
              error={Boolean(quoteFormErrors?.[ValidationKey.HIICUConfinementBenefitAmount])}
              helperText={quoteFormErrors ? quoteFormErrors?.[ValidationKey.HIICUConfinementBenefitAmount] : undefined}
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: intensiveCareAdmissionPaths.paymentFrequencyLimitCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIICUConfinementBenefit.paymentFrequencyLimitCd"
              label="Payment Frequency Limit"
              id={intensiveCareAdmissionPaths.paymentFrequencyLimitCd}
              value={getQuoteFieldValue<number>(intensiveCareAdmissionPaths.paymentFrequencyLimitCd)}
              onChange={e =>
                updateQuoteFieldValue(intensiveCareAdmissionPaths.paymentFrequencyLimitCd, e.target.value as string)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const DailyIntensiveCareConfinement = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();

  const dailyIntensiveCareConfinementPaths = getDailyIntensiveCareConfinementPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'daily-intensive-care-confinement-row-1'},
      items: [
        {
          key: dailyIntensiveCareConfinementPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={getQuoteFieldValue(dailyIntensiveCareConfinementPaths.benefitAmount)}
              type="number"
              id={dailyIntensiveCareConfinementPaths.benefitAmount}
              adornment="money"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'daily-intensive-care-max-days-limit-confinement',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIICUDailyConfinementBenefit.maxBenefitNumber"
              label="Max # Days Limit (Per Confinement)"
              value={getQuoteFieldValue(dailyIntensiveCareConfinementPaths.maxBenefitNumber)}
              id={dailyIntensiveCareConfinementPaths.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const StepDownIntensiveCareAdmission = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();

  const stepdownIntensiveCareAdmissionPaths = getStepdownIntensiveCareAdmissionPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'stepdown-intensive-care-admission-row-1'},
      items: [
        {
          key: stepdownIntensiveCareAdmissionPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={getQuoteFieldValue(stepdownIntensiveCareAdmissionPaths.benefitAmount)}
              type="number"
              id={stepdownIntensiveCareAdmissionPaths.benefitAmount}
              adornment="money"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: stepdownIntensiveCareAdmissionPaths.paymentFrequencyLimitCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIICUStepDownAdmissionBenefit.paymentFrequencyLimitCd"
              label="Payment Frequency Limit"
              value={getQuoteFieldValue(stepdownIntensiveCareAdmissionPaths.paymentFrequencyLimitCd)}
              id={stepdownIntensiveCareAdmissionPaths.paymentFrequencyLimitCd}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const DailyStepDownIntensiveCareConfinement = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();

  const dailyStepdownIntensiveCareConfinementPaths = getDailyStepdownIntensiveCareConfinementPaths(
    selectedPlan?.planIndex
  );

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'daily-stepdown-intensive-care-confinement-row-1'},
      items: [
        {
          key: dailyStepdownIntensiveCareConfinementPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={getQuoteFieldValue(dailyStepdownIntensiveCareConfinementPaths.benefitAmount)}
              type="number"
              id={dailyStepdownIntensiveCareConfinementPaths.benefitAmount}
              disabled
              required
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: dailyStepdownIntensiveCareConfinementPaths.maxBenefitNumber,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIICUDailyStepDownConfinementBenefit.maxBenefitNumber"
              label="Max # Days Limit (Per Confinement)"
              value={getQuoteFieldValue(dailyStepdownIntensiveCareConfinementPaths.maxBenefitNumber)}
              id={dailyStepdownIntensiveCareConfinementPaths.maxBenefitNumber}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const ObservationUnit = () => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue} = useQuoteField();

  const observationUnitPaths = getObservationUnitPaths(selectedPlan?.planIndex);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'observation-unit-row-1'},
      items: [
        {
          key: observationUnitPaths.benefitStructure,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIObservationUnit.benefitStructure"
              label="Benefit Structure"
              value={getQuoteFieldValue(observationUnitPaths.benefitStructure)}
              id={observationUnitPaths.benefitStructure}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-2'},
      items: [
        {
          key: observationUnitPaths.benefitAmount,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              value={getQuoteFieldValue(observationUnitPaths.benefitAmount)}
              type="number"
              id={observationUnitPaths.benefitAmount}
              required
              disabled
              adornment="money"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-3'},
      hidden: true,
      items: [
        {
          key: 'observation-unit-benefit-low-amount',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Low Amount"
              value={''}
              type="number"
              id={'observationUnitBenefitLowAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'observation-unit-benefit-high-amount',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit High Amount"
              value=""
              type="number"
              id={'observationUnitBenefitHighAmount'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-4'},
      hidden: true,
      items: [
        {
          key: 'observation-unit-break-point',
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Break Point (Hours)"
              options={[]}
              value="1"
              id={'observationUnitBreakPoint'}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-5'},
      items: [
        {
          key: observationUnitPaths.mindurationCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIObservationUnit.mindurationCd"
              label="Duration (Min Hours)"
              value={getQuoteFieldValue(observationUnitPaths.mindurationCd)}
              id={observationUnitPaths.mindurationCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: observationUnitPaths.maxdurationCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIObservationUnit.maxdurationCd"
              label="Duration (Max Hours)"
              value={getQuoteFieldValue(observationUnitPaths.maxdurationCd)}
              id={observationUnitPaths.maxdurationCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-6'},
      items: [
        {
          key: observationUnitPaths.paymentFrequencyLimitCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIObservationUnit.paymentFrequencyLimitCd"
              label="Payment Frequency Limit"
              id={observationUnitPaths.paymentFrequencyLimitCd}
              value={getQuoteFieldValue(observationUnitPaths.paymentFrequencyLimitCd)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: observationUnitPaths.separationPeriodCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIObservationUnit.separationPeriodCd"
              label="Separation Period (Days)"
              value={getQuoteFieldValue(observationUnitPaths.separationPeriodCd)}
              id={observationUnitPaths.separationPeriodCd}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'observation-unit-row-7'},
      items: [
        {
          key: observationUnitPaths.isPrePostOperativeCareApplied,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="checkbox"
              value={getQuoteFieldValue(observationUnitPaths.isPrePostOperativeCareApplied)}
              checked={getQuoteFieldValue(observationUnitPaths.isPrePostOperativeCareApplied)}
              label="Pre/Post Operative Care"
              id={observationUnitPaths.isPrePostOperativeCareApplied}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const HospitalCareServicesBlock = () => {
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();

  const {isHosAdmissionApplied, hosAdmissionDetails} = getHospitalCareServicesPropertyPaths(selectedPlan?.planIndex, 0);
  const {isHospitalAdmissionRoutineNewbornApplied} = getHosAdmissionRoutineNewbornPropertyPaths(
    selectedPlan?.planIndex,
    0
  );
  const {isHosConfinementApplied, hosConfinementDetails} = getDailyHospitalConfinementPropertyPaths(
    selectedPlan?.planIndex,
    0
  );
  const {isHospitalConfinementRoutineNewbornApplied} = getHosConfinementRoutineNewbornPropertyPaths(
    selectedPlan?.planIndex,
    0
  );
  const {isICUConfinementApplied, icuConfinementDetails} = getIntensiveCareAdmissionPaths(selectedPlan?.planIndex, 0);
  const {isObservationUnitApplied} = getObservationUnitPaths(selectedPlan?.planIndex, 0);

  const {isStepDownIntensiveCareAdmissionApplied} = getStepdownIntensiveCareAdmissionPaths(selectedPlan?.planIndex);
  const {isDailyStepDownIntensiveCareConfinmentApplied} = getDailyStepdownIntensiveCareConfinementPaths(
    selectedPlan?.planIndex
  );

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);
  const coverageDefId = getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[0]._key.rootId`
  );

  const uniqueKeyForOptions = `${getQuoteFieldValue(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.id`
  )}-${selectedPlan?.planIndex}`;

  const onToggleBenefitApply = (
    benefitAppliedPath: string,
    value: boolean,
    benefitDetailsPath: string,
    benefitDetailsObjectName: string
  ) => {
    const fieldsToUpdate = [
      {
        path: benefitAppliedPath,
        newValue: value,
      },
    ];
    if (!isDefined(getQuoteFieldValue(benefitDetailsPath))) {
      const benefitDetails = getBenefitDetailsObject(
        benefitDetailsObjectName,
        defaultPlanValues?.get('GHI3'),
        createGHI3BenefitStructure,
        planRootId,
        coverageDefId
      );
      fieldsToUpdate.push({
        path: benefitDetailsPath,
        newValue: fromJS(benefitDetails),
      });
    }
    updateQuoteFieldValues(fieldsToUpdate);
  };

  const hospitalCareServices: ConditionalSection[] = [
    {
      Component: HospitalAdmission,
      key: 'HospitalAdmission',
      label: 'HOSPITAL ADMISSION',
      isApplied: getQuoteFieldValue(isHosAdmissionApplied),
      disabled: true,
      onToggleVisibility: value =>
        onToggleBenefitApply(isHosAdmissionApplied, value, hosAdmissionDetails, 'hosAdmissionDetails'),
    },
    {
      Component: HospitalAdmissionRoutineNewborn,
      key: 'HospitalAdmissionRoutineNewborn',
      label: 'HOSPITAL ADMISSION - ROUTINE NEWBORN',
      hidden: !getQuoteFieldValue(isHospitalAdmissionRoutineNewbornApplied),
      isApplied: getQuoteFieldValue(isHospitalAdmissionRoutineNewbornApplied),
      disabled: true,
    },
    {
      Component: DailyHospitalConfinement,
      key: 'DailyHospitalConfinement',
      label: 'DAILY HOSPITAL CONFINEMENT',
      isApplied: getQuoteFieldValue(isHosConfinementApplied),
      disabled: true,
      onToggleVisibility: value =>
        onToggleBenefitApply(isHosAdmissionApplied, value, hosConfinementDetails, 'hosConfinementDetails'),
    },
    {
      Component: DailyHospitalConfinementRoutineNewborn,
      key: 'DailyHospitalConfinementRoutineNewborn',
      label: 'DAILY HOSPITAL CONFINEMENT - ROUTINE NEWBORN',
      hidden: !getQuoteFieldValue(isHospitalConfinementRoutineNewbornApplied),
      isApplied: getQuoteFieldValue(isHospitalConfinementRoutineNewbornApplied),
      disabled: true,
    },
    {
      Component: IntensiveCareAdmission,
      key: 'IntensiveCareAdmission',
      label: 'INTENSIVE CARE ADMISSION',
      isApplied: getQuoteFieldValue(isICUConfinementApplied),
      disabled: true,
      onToggleVisibility: value =>
        onToggleBenefitApply(isICUConfinementApplied, value, icuConfinementDetails, 'icuConfinementDetails'),
    },
    {
      Component: DailyIntensiveCareConfinement,
      key: 'DailyIntensiveCareConfinement',
      label: 'DAILY INTENSIVE CARE CONFINEMENT',
      isApplied: true,
      disabled: true,
    },
    {
      Component: StepDownIntensiveCareAdmission,
      key: 'StepDownIntensiveCareAdmission',
      label: 'STEP DOWN INTENSIVE CARE ADMISSION',
      disabled: true,
      isApplied: getQuoteFieldValue(isStepDownIntensiveCareAdmissionApplied),
    },
    {
      Component: DailyStepDownIntensiveCareConfinement,
      key: 'DailyStepDownIntensiveCareConfinement',
      label: 'DAILY STEP DOWN INTENSIVE CARE CONFINEMENT',
      isApplied: getQuoteFieldValue(isDailyStepDownIntensiveCareConfinmentApplied),
      disabled: true,
    },
    {
      Component: ObservationUnit,
      key: 'ObservationUnit',
      label: 'OBSERVATION UNIT',
      isApplied: getQuoteFieldValue(isObservationUnitApplied), // Not applied for NJ state, as per requirement
      disabled: true,
    },
  ];

  return (
    <FormWrapper title="Hospital Care Services" collapsible withSeparator expanded>
      <Grid container direction="column">
        <HospitalCareServicesBenefitOptionSelection key={uniqueKeyForOptions} />
        <QuoteFormConditionalSection sections={hospitalCareServices} />
      </Grid>
    </FormWrapper>
  );
};
/* End: Hospital Care Services */

const getMoneyObject = (value: number) => ({_type: 'Money', amount: value, currency: 'USD'});

const getICUConfinementBenefitDetails = (
  amountValue: number,
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  coverageDefId: string,
  selectedPlanIndex: number = 0,
  selectedCoverageIndex: number = 0
) => {
  const icuConfinementDetailsPaths = getIntensiveCareAdmissionPaths(selectedPlanIndex, selectedCoverageIndex);

  if (amountValue === 0) {
    return [
      {
        path: icuConfinementDetailsPaths.icuConfinementDetails,
        newValue: undefined,
      },
      {
        path: icuConfinementDetailsPaths.isICUConfinementApplied,
        newValue: false,
      },
    ];
  }

  const benefitStructure = createGHI3BenefitStructure(defaultValues, planRootId, coverageDefId);

  const defaultIcuConfinementDetails = [
    {
      path: icuConfinementDetailsPaths.icuConfinementDetails,
      newValue: fromJS({
        ...benefitStructure.icuConfinementDetails,
        benefitAmount: {_type: 'Money', amount: amountValue, currency: 'USD'},
        paymentFrequencyLimitCd: '1/Year',
      }),
    },
    {
      path: icuConfinementDetailsPaths.isICUConfinementApplied,
      newValue: true,
    },
  ];

  return defaultIcuConfinementDetails;
};
